/**
 * "Violated" edges: a predecessor → successor relation or an
 * activity → milestone connection whose elements are in the wrong order. A
 * force move (Shift) keeps such edges instead of deleting them; violated is
 * derived from the dates — never persisted — and heals by itself as soon as the
 * order is correct again.
 *
 * Day granularity everywhere: elements on the same day are in order. The
 * canvas compares map positions (days from the project start), the date
 * pickers compare ISO deadline strings — both variants live here so the
 * painters, the drag clamps, the picker windows and the backend rules agree.
 */

/** Day part of an ISO date-time ('' stays '' — template maps carry no deadlines). */
export function deadlineDay(deadline: string): string {
  return deadline.split('T')[0];
}

/** A relation is violated when the predecessor ends after the successor (map positions). */
export function isRelationViolatedByPosition(
  predecessorPosition: number,
  successorPosition: number,
): boolean {
  return predecessorPosition > successorPosition;
}

/** A relation is violated when the predecessor ends after the successor (ISO deadlines). */
export function isRelationViolatedByDeadline(
  predecessorDeadline: string,
  successorDeadline: string,
): boolean {
  return deadlineDay(predecessorDeadline) > deadlineDay(successorDeadline);
}

/**
 * A milestone connection is violated when the activity is not in the
 * milestone's phase or ends after the milestone (map positions).
 */
export function isConnectionViolatedByPosition(
  activity: { phase: string; originalPosition: number },
  milestone: { phase: string; originalPosition: number },
): boolean {
  return (
    activity.phase !== milestone.phase ||
    activity.originalPosition > milestone.originalPosition
  );
}

/**
 * A milestone connection is violated when the activity is not in the
 * milestone's phase or ends after the milestone (ISO deadlines).
 */
export function isConnectionViolatedByDeadline(
  activity: { phase: string; deadline: string },
  milestone: { phase: string; deadline: string },
): boolean {
  return (
    activity.phase !== milestone.phase ||
    deadlineDay(activity.deadline) > deadlineDay(milestone.deadline)
  );
}
